import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';
// import BrazilianLogo from './BrazilianLogo';
import colors from '../styles/colors';

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  transition: all 0.3s ease;
  padding: 1rem 0;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;


const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.li`
  a {
    text-decoration: none;
    color: ${colors.text.primary};
    font-weight: 500;
    transition: color 0.3s ease;
    position: relative;

    &:hover {
      color: ${colors.primary.green};
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 0;
      height: 2px;
      background: ${colors.primary.green};
      transition: width 0.3s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${colors.text.primary};

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
`;

const MobileNavLinks = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  text-align: center;
`;

const MobileNavLink = styled.li`
  a {
    text-decoration: none;
    color: ${colors.text.primary};
    font-size: 1.5rem;
    font-weight: 500;
    transition: color 0.3s ease;

    &:hover {
      color: ${colors.primary.green};
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: ${colors.text.primary};
`;

const Header = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleReservation = () => {
    const whatsappNumber = "393331054944";
    const message = "Ciao! Vorrei prenotare un tavolo al Cuore Brasiliano. Grazie!";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'menu', href: '#menu' },
    { key: 'about', href: '#about' },
    { key: 'contact', href: '#contact' },
    { key: 'reservation', href: '#reservation', onClick: handleReservation }
  ];

  return (
    <>
      <HeaderContainer
        $scrolled={scrolled}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Nav>
          <Logo>
            <LogoImage src="./logo.png" alt="Cuore Brasiliano" />
          </Logo>
          
          <NavLinks>
            {navItems.map(item => (
              <NavLink key={item.key}>
                {item.onClick ? (
                  <a href="#" onClick={(e) => { e.preventDefault(); item.onClick(); }}>
                    {t(`header.${item.key}`)}
                  </a>
                ) : (
                  <a href={item.href}>{t(`header.${item.key}`)}</a>
                )}
              </NavLink>
            ))}
          </NavLinks>

          <LanguageSwitcher />
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            ☰
          </MobileMenuButton>
        </Nav>
      </HeaderContainer>

      {mobileMenuOpen && (
        <MobileMenu
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <CloseButton onClick={toggleMobileMenu}>×</CloseButton>
          <MobileNavLinks>
            {navItems.map(item => (
              <MobileNavLink key={item.key}>
                {item.onClick ? (
                  <a href="#" onClick={(e) => { e.preventDefault(); item.onClick(); setMobileMenuOpen(false); }}>
                    {t(`header.${item.key}`)}
                  </a>
                ) : (
                  <a href={item.href} onClick={() => setMobileMenuOpen(false)}>
                    {t(`header.${item.key}`)}
                  </a>
                )}
              </MobileNavLink>
            ))}
          </MobileNavLinks>
          <LanguageSwitcher />
        </MobileMenu>
      )}
    </>
  );
};

export default Header;
